import { useState, useRef, useEffect } from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  Stack,
  Group,
  Button,
  Alert,
  Stepper,
  Divider,
  Card,
  Image,
  ActionIcon,
  Center,
  Progress,
  TextInput,
  SimpleGrid,
  Badge,
  Loader
} from '@mantine/core';
import {
  IconCamera,
  IconUpload,
  IconCheck,
  IconX,
  IconExclamationCircle,
  IconArrowLeft,
  IconArrowRight,
  IconRefresh,
  IconId,
  IconScan,
  IconUser
} from '@tabler/icons-react';
import { useApiClient } from '../../api';
import { uploadIdVerification } from '../../api/uploads';

const IDVerification = ({ onComplete, onCancel, loading = false }) => {
  const client = useApiClient();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [scanningBarcode, setScanningBarcode] = useState(false);
  const [barcodeData, setBarcodeData] = useState(null);
  const [images, setImages] = useState({
    selfie: null,
    front: null,
    back: null
  });

  const fileInputRefs = {
    selfie: useRef(null),
    front: useRef(null),
    back: useRef(null)
  };

  const steps = [
    {
      label: 'Selfie with ID',
      description: 'Hold your ID next to your face',
      key: 'selfie',
      instruction: 'Take a clear photo of yourself holding your government-issued ID next to your face. Make sure both your face and ID are clearly visible.'
    },
    {
      label: 'ID Front',
      description: 'Front of government ID',
      key: 'front',
      instruction: 'Take a photo of the front of your government-issued ID. Ensure all text is clearly readable and the image is not blurry.'
    },
    {
      label: 'ID Back',
      description: 'Back of government ID',
      key: 'back',
      instruction: 'Take a photo of the back of your government-issued ID. We will scan for barcodes to verify the information.'
    }
  ];

  // Clean up blob URLs when component unmounts or images change
  useEffect(() => {
    return () => {
      Object.values(images).forEach(image => {
        if (image?.preview?.startsWith('blob:')) {
          URL.revokeObjectURL(image.preview);
        }
      });
    };
  }, [images]);

  const handleFileSelect = async (stepKey, file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file (JPG, PNG, GIF, WebP)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image file is too large. Maximum size is 10MB.');
      return;
    }

    setError(null);
    setUploading(true);

    try {
      // Clean up previous preview URL if it exists
      const previousImage = images[stepKey];
      if (previousImage?.preview?.startsWith('blob:')) {
        URL.revokeObjectURL(previousImage.preview);
      }

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);

      const imageData = {
        file: file,
        preview: previewUrl,
        name: file.name,
        size: file.size,
        timestamp: Date.now()
      };

      setImages(prev => ({
        ...prev,
        [stepKey]: imageData
      }));

      // If this is the back of ID, attempt barcode scanning
      if (stepKey === 'back') {
        await scanBarcode(file, previewUrl);
      }

    } catch (err) {
      console.error('Error processing file:', err);
      setError('Error processing the selected image.');
    } finally {
      setUploading(false);
    }
  };

  // Frontend barcode scanning is stubbed out — the backend extracts barcode
  // data during verification processing (see the commented Quagga code below).
  const scanBarcode = async () => {
    setScanningBarcode(true);
    setBarcodeData(null);

    // Since Quagga doesn't properly support PDF417, we'll skip frontend scanning
    // and let the backend handle all barcode processing
    setTimeout(() => {
      setBarcodeData({
        message: 'Barcode scanning will be processed on the server',
        note: 'ID verification will continue - barcode data will be extracted during backend processing',
        frontend_scan: false
      });
      setScanningBarcode(false);
    }, 1500); // Show a brief processing animation

    return;

    // The original Quagga implementation is commented out to prevent errors
    /*
    try {
      const scanPromise = new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Barcode scanning timeout'));
        }, 8000);

        Quagga.decodeSingle({
          decoder: {
            readers: [
              "code_128_reader",
              "code_39_reader",
              "ean_reader",
              "ean_8_reader"
            ]
          },
          locate: true,
          src: previewUrl,
          inputStream: {
            size: 800
          }
        }, (result) => {
          clearTimeout(timeout);
          if (result && result.codeResult) {
            resolve(result.codeResult.code);
          } else {
            resolve(null);
          }
        });
      });

      const barcodeResult = await scanPromise;

      if (barcodeResult) {
        const parsedData = parseIDBarcode(barcodeResult);
        setBarcodeData(parsedData);
      } else {
        setBarcodeData({
          message: 'No barcode detected',
          note: 'Backend processing will handle ID data extraction'
        });
      }
    } catch (err) {
      console.error('Barcode scanning error:', err);
      setBarcodeData({
        message: 'Barcode processing moved to server',
        note: 'Your ID will be processed during verification'
      });
    } finally {
      setScanningBarcode(false);
    }
    */
  };

  const handleRemoveImage = (stepKey) => {
    const image = images[stepKey];
    if (image?.preview?.startsWith('blob:')) {
      URL.revokeObjectURL(image.preview);
    }

    setImages(prev => ({
      ...prev,
      [stepKey]: null
    }));

    if (stepKey === 'back') {
      setBarcodeData(null);
    }
  };

  const triggerFileInput = (stepKey) => {
    fileInputRefs[stepKey].current?.click();
  };

  const canProceedToNext = () => {
    const currentStepKey = steps[activeStep].key;
    return images[currentStepKey] !== null;
  };

  const nextStep = () => {
    if (canProceedToNext() && activeStep < steps.length - 1) {
      setActiveStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (activeStep > 0) {
      setActiveStep(prev => prev - 1);
    }
  };

  const handleComplete = async () => {
    try {
      setUploading(true);
      setError(null);

      // Upload files via the API multipart endpoint (docs/api-contract.md §4.9).
      // Frontend barcode scanning is disabled — only send real scan data.
      const effectiveBarcodeData = barcodeData?.frontend_scan === false ? null : barcodeData;

      const response = await uploadIdVerification(client, {
        selfieWithId: images.selfie?.file,
        frontOfId: images.front?.file,
        backOfId: images.back?.file,
        barcodeData: effectiveBarcodeData
      });

      if (onComplete) {
        onComplete(response);
      }
    } catch (error) {
      console.error('Error submitting ID verification:', error);
      setError(error.message || 'Failed to submit ID verification. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const isComplete = () => {
    return images.selfie && images.front && images.back;
  };

  const renderImageUpload = (stepKey) => {
    const image = images[stepKey];
    const step = steps[activeStep];

    return (
      <Stack gap="md">
        {/* Instruction */}
        <Alert color="pink" icon={<IconCamera size="1rem" />}>
          <Text size="sm">{step.instruction}</Text>
        </Alert>

        {/* Upload Area */}
        <Card
          shadow="sm"
          padding="xl"
          radius="md"
          withBorder
          style={{
            cursor: image ? 'default' : 'pointer',
            borderStyle: image ? 'solid' : 'dashed',
            minHeight: 300
          }}
          onClick={!image ? () => triggerFileInput(stepKey) : undefined}
        >
          {image ? (
            <Stack gap="md">
              <div style={{ position: 'relative' }}>
                <Image
                  src={image.preview}
                  alt={`${stepKey} upload`}
                  fit="contain"
                  height={250}
                  radius="sm"
                />
                <ActionIcon
                  color="red"
                  size="lg"
                  variant="filled"
                  onClick={() => handleRemoveImage(stepKey)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8
                  }}
                >
                  <IconX size="1rem" />
                </ActionIcon>
              </div>
              <Text size="sm" c="dimmed" ta="center">
                {image.name} ({(image.size / 1024 / 1024).toFixed(2)} MB)
              </Text>
            </Stack>
          ) : (
            <Center style={{ height: 200 }}>
              <Stack gap="md" align="center">
                <IconUpload size="3rem" color="var(--mantine-color-gray-5)" />
                <Text size="lg" c="dimmed" ta="center">
                  Click to upload {step.label.toLowerCase()}
                </Text>
                <Text size="sm" c="dimmed" ta="center">
                  Supported: JPG, PNG, GIF, WebP (max 10MB)
                </Text>
              </Stack>
            </Center>
          )}
        </Card>

        {/* Upload Button */}
        <Group gap="md">
          <Button
            leftSection={<IconUpload size="1rem" />}
            onClick={() => triggerFileInput(stepKey)}
            variant={image ? "outline" : "filled"}
            loading={uploading}
          >
            {image ? 'Replace Image' : 'Select Image'}
          </Button>
        </Group>

        {/* Barcode Scanning Results (only for back step) */}
        {stepKey === 'back' && image && (
          <Card shadow="xs" padding="md" radius="md" withBorder>
            <Stack gap="md">
              <Group gap="sm">
                <IconScan size="1.2rem" />
                <Text weight={500}>Barcode Scan Results</Text>
                {scanningBarcode && <Loader size="sm" />}
              </Group>

              {scanningBarcode && (
                <div>
                  <Text size="sm" c="pink">Scanning for barcode...</Text>
                  <Progress value={100} animated color="pink" size="sm" mt="xs" />
                </div>
              )}

              {barcodeData && !scanningBarcode && (
                <Stack gap="xs">
                  {barcodeData.message ? (
                    <Text size="sm" c="dimmed">{barcodeData.message}</Text>
                  ) : (
                    <SimpleGrid cols={2} spacing="xs">
                      {Object.entries(barcodeData).map(([key, value]) => (
                        <TextInput
                          key={key}
                          label={key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                          value={value || ''}
                          readOnly
                          size="xs"
                          variant="filled"
                        />
                      ))}
                    </SimpleGrid>
                  )}
                </Stack>
              )}

              {!scanningBarcode && !barcodeData && image && (
                <Group gap="sm">
                  <Text size="sm" c="dimmed">No barcode scan attempted yet</Text>
                  <Button
                    size="xs"
                    variant="subtle"
                    leftSection={<IconRefresh size="0.8rem" />}
                    onClick={() => scanBarcode(image.file, image.preview)}
                  >
                    Scan Again
                  </Button>
                </Group>
              )}
            </Stack>
          </Card>
        )}

        {/* Hidden File Input */}
        <input
          ref={fileInputRefs[stepKey]}
          type="file"
          accept="image/*"
          onChange={(e) => handleFileSelect(stepKey, e.target.files[0])}
          style={{ display: 'none' }}
        />
      </Stack>
    );
  };

  return (
    <Container size="md" py="xl">
      <Paper shadow="sm" radius="md" p="xl">
        <Stack gap="xl">
          {/* Header */}
          <div>
            <Group gap="md" mb="sm">
              <IconId size="2rem" />
              <Title order={1}>ID Verification</Title>
            </Group>
            <Text c="dimmed">
              Please provide clear photos of your government-issued ID for verification purposes.
            </Text>
          </div>

          {/* Progress Stepper */}
          <Stepper active={activeStep} size="sm" radius="md">
            {steps.map((step) => (
              <Stepper.Step
                key={step.key}
                label={step.label}
                description={step.description}
                icon={images[step.key] ? <IconCheck size="1rem" /> : undefined}
              />
            ))}
            <Stepper.Completed>
              <Stack align="center" gap="md" py="xl">
                <IconCheck size="4rem" color="green" />
                <Title order={2}>Verification Complete!</Title>
                <Text ta="center" c="dimmed">
                  All required images have been uploaded. You can now submit your verification.
                </Text>
              </Stack>
            </Stepper.Completed>
          </Stepper>

          <Divider />

          {/* Error Alert */}
          {error && (
            <Alert icon={<IconExclamationCircle size="1rem" />} color="red" onClose={() => setError(null)} withCloseButton>
              {error}
            </Alert>
          )}

          {/* Step Content */}
          {activeStep < steps.length && renderImageUpload(steps[activeStep].key)}

          {/* Navigation */}
          <Group justify="space-between" pt="md">
            <Group gap="sm">
              {onCancel && (
                <Button variant="subtle" onClick={onCancel} leftSection={<IconX size="1rem" />}>
                  Cancel
                </Button>
              )}
              {activeStep > 0 && (
                <Button variant="outline" onClick={prevStep} leftSection={<IconArrowLeft size="1rem" />}>
                  Previous
                </Button>
              )}
            </Group>

            <Group gap="sm">
              {/* Progress Badge */}
              <Badge color="pink" variant="light">
                {Object.values(images).filter(Boolean).length} / 3 uploaded
              </Badge>

              {activeStep < steps.length - 1 ? (
                <Button
                  onClick={nextStep}
                  disabled={!canProceedToNext()}
                  rightSection={<IconArrowRight size="1rem" />}
                >
                  Next
                </Button>
              ) : (
                <Button
                  onClick={handleComplete}
                  disabled={!isComplete()}
                  loading={loading || uploading}
                  leftSection={<IconCheck size="1rem" />}
                  color="green"
                >
                  Complete Verification
                </Button>
              )}
            </Group>
          </Group>
        </Stack>
      </Paper>
    </Container>
  );
};

export default IDVerification;
